import type { Game } from '../api/types';
import { ApiError } from '../api/client';

export type FundingChoice = 'real' | 'bonus';

export interface AggLaunchStatus {
  enabled: boolean;
  real_money_enabled: boolean;
  api_configured: boolean;
  demo_launch_allowed?: boolean;
  bonus_play_enabled?: boolean;
  bonus_launch_allowed?: boolean;
}

export function looksLikeVerifiedSlot(game: Game): boolean {
  const raw = `${game.game_type || ''} ${game.category || ''}`.toLowerCase();
  if (!raw.trim()) return false;
  if (
    /\blive\b|\bsport|\btable|\bcrash|\bpoker|\broulette|\bblackjack|\bbaccarat|\bfishing|\blottery|\bkeno|\bingo|\binstant\b|\bmines\b|\bskill\b/.test(
      raw,
    )
  ) {
    return false;
  }
  return /\bslot/.test(raw);
}

export function isSlotCategory(game: Game): boolean {
  const raw = `${game.game_type || ''} ${game.category || ''}`.toLowerCase();
  return /\bslot/.test(raw);
}

export function launchErrorMessage(
  err: unknown,
  fallback: string,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  const detail =
    err instanceof ApiError && err.body && typeof err.body === 'object' && err.body && 'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : err instanceof Error
        ? err.message
        : '';
  if (detail === 'bonus_play_disabled') return t('games:bonusPlayDisabled');
  if (detail === 'real_money_disabled') return t('games:realMoneyDisabled');
  if (detail === 'game_not_bonus_eligible') return t('games:bonusNotSlot');
  if (detail === 'AGGREGATOR_SESSION_FUNDING_UNSUPPORTED') return t('games:sessionFundingUnsupported');
  if (detail === 'demo_session_failed' || detail === 'aggregator_disabled') return t('games:demoUnavailable');
  return detail || fallback;
}

/** Decide default funding without auto-switching to bonus. */
export function pickDefaultFunding(opts: {
  agg: AggLaunchStatus | null;
  game: Game;
  loggedIn: boolean;
  realMinor: number;
  bonusMinor: number;
}): { mode: 'demo' | 'real' | 'bonus_prompt' | 'login' | 'blocked'; reason?: string } {
  const { agg, game, loggedIn, realMinor, bonusMinor } = opts;
  const demoReady = Boolean(agg?.demo_launch_allowed ?? (agg?.enabled && agg?.api_configured));
  const realReady = Boolean(agg?.enabled && agg?.api_configured && agg?.real_money_enabled && loggedIn);
  const bonusReady = Boolean(
    realReady && agg?.bonus_launch_allowed && looksLikeVerifiedSlot(game) && bonusMinor > 0,
  );

  if (!agg?.enabled || !agg?.api_configured) {
    return { mode: 'blocked', reason: 'aggregator_disabled' };
  }

  if (realReady) {
    if (realMinor > 0) return { mode: 'real' };
    if (bonusReady) return { mode: 'bonus_prompt' };
    // Real money on but empty wallets — still attempt real (server returns insufficient) or block.
    return { mode: 'real' };
  }

  if (!loggedIn) {
    if (demoReady && game.has_demo) return { mode: 'demo' };
    return { mode: 'login' };
  }

  if (demoReady && game.has_demo) return { mode: 'demo' };
  return { mode: 'blocked', reason: 'real_money_disabled' };
}

export const CATALOG_SCROLL_KEY = 'vbb_catalog_scroll_y';
export const CATALOG_RETURN_KEY = 'vbb_catalog_return_path';

export function rememberCatalogPosition(pathname: string) {
  try {
    sessionStorage.setItem(CATALOG_SCROLL_KEY, String(window.scrollY || 0));
    sessionStorage.setItem(CATALOG_RETURN_KEY, pathname || '/');
  } catch {
    // ignore
  }
}

export function restoreCatalogPosition() {
  try {
    const y = Number(sessionStorage.getItem(CATALOG_SCROLL_KEY) || '0');
    sessionStorage.removeItem(CATALOG_SCROLL_KEY);
    if (Number.isFinite(y) && y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  } catch {
    // ignore
  }
}

export function catalogReturnPath(): string {
  try {
    return sessionStorage.getItem(CATALOG_RETURN_KEY) || '/';
  } catch {
    return '/';
  }
}
